import { useEffect, useState } from "react";
import Card from "src/components/app/Card/Card";
import { Wallet } from "lucide-react";
import Checkbox from "../common/Checkbox";
import { getWalletUsageMessage } from "src/helpers/walletHelpers";
import { calculateWalletUsage } from "src/helpers/walletHelpers";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as walletActions from "src/redux/slices/walletSlice";

interface WalletUsageSectionProps {
  bookingAmount: number | string;
  discountAmount: number | string;
  onUsedWalletMoney: (usedWalletMoney: number) => void;
}

const WalletUsageSection: React.FC<WalletUsageSectionProps> = ({
  bookingAmount,
  discountAmount = 0,
  onUsedWalletMoney,
}) => {
  const contactNo = useAppSelector(
    (state) => state.auth.patient.data?.contact_no
  );
  const [useWalletMoney, setUseWalletMoney] = useState<boolean>(false);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const { coinsValue } = useAppSelector((state) => state.wallet);
  const dispatch = useAppDispatch();

  useEffect(() => {
    fetchWalletAmount();
  }, []);

  useEffect(() => {
    if (useWalletMoney) {
      const actualWalletUsage = getWalletUsage();

      dispatch(walletActions.setCurrentUsedWalletMoney(actualWalletUsage));
    }
  }, [useWalletMoney, walletBalance, bookingAmount, discountAmount]);

  const fetchWalletAmount = () => {
    new Promise((resolve, reject) => {
      dispatch(
        walletActions.fetchWalletAmountRequest({
          payload: { contact_no: contactNo },
          resolve,
          reject,
        })
      );
    })
      .then((res: any) => {
        let coins = Number(res?.data?.balance ?? 0) / 100;
        if (coinsValue) {
          coins = coins * coinsValue;
        }
        setWalletBalance(coins);
      })
      .catch((error) => {
        console.error("Error fetching wallet amount:", error);
      });
  };

  // Helper function to calculate wallet usage with minimum 1 rupee payment
  const getWalletUsage = () => {
    const finalAmountAfterDiscount =
      Number(bookingAmount) - Number(discountAmount || 0);
    return calculateWalletUsage(walletBalance, finalAmountAfterDiscount);
  };

  return (
    <div>
      {/* Wallet Money Section */}
      {walletBalance !== 0 && walletBalance > 0 && (
        <div>
          <h2 className="text-md font-semibold mb-2">Your Wallet</h2>
          <Card stackTitleAndDescription={true}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="bg-blue-100 text-blue-600 p-1 rounded-full text-sm">
                  <Wallet className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium">
                  Use Wallet Money (₹{walletBalance})
                </span>
              </div>
              <Checkbox
                label=""
                checked={useWalletMoney}
                onChange={(checked) => {
                  setUseWalletMoney(checked);
                  if (checked) {
                    onUsedWalletMoney && onUsedWalletMoney(getWalletUsage());
                  } else {
                    onUsedWalletMoney && onUsedWalletMoney(0);
                  }
                }}
                className="m-0"
              />
            </div>
            {useWalletMoney && walletBalance > 0 && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      Wallet Balance Available
                    </p>
                    <p className="text-xs text-gray-600">
                      {getWalletUsageMessage(
                        walletBalance,
                        Number(bookingAmount) - Number(discountAmount || 0)
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-blue-600">
                      -₹{getWalletUsage()}
                    </p>
                    <p className="text-xs text-gray-500">Deducted</p>
                  </div>
                </div>
              </div>
            )}
            {useWalletMoney && walletBalance === 0 && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 text-center">
                  No wallet balance available
                </p>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default WalletUsageSection;
